import { Button } from '@kerfjs/ui/button';
import { Checkbox } from '@kerfjs/ui/checkbox';
import { List } from '@kerfjs/ui/list';
import { Row } from '@kerfjs/ui/row';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import { orderDay, receiptSummary, roundQuantity } from '../../../../shared/fulfilment.js';
import type { LineOrderT } from '../../../data/api.js';

/**
 * Record a delivery against a line's order (FR-5.19, HS2-6AJZPE): the quantity received (default:
 * the rest outstanding), the date, and a note for a short or damaged delivery. "Close the rest short"
 * ends the order with what arrived. Earlier receipts are listed and can be removed if entered by
 * mistake; an order closed short can be reopened.
 */
export function ReceiptDialog({
  lineNumber,
  lineDescription,
  order,
  today,
}: {
  lineNumber: number;
  lineDescription: string;
  order: LineOrderT;
  /** Today as `YYYY-MM-DD`, the default receipt date. */
  today: string;
}): SafeHtml {
  const outstanding =
    order.quantityOrdered === null ? null : Math.max(0, roundQuantity(order.quantityOrdered - order.receivedQuantity));
  const progress = receiptSummary(order);
  return (
    <DialogSurface
      label={`Record Receipt · Line ${String(lineNumber)}`}
      className="receipt-dialog"
      lightDismiss
      rootAttributes={{ 'data-unsaved-guard': 'this receipt', 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action="receipt-cancel"
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            {order.closedShort ? 'Close' : 'Cancel'}
          </Button>
          {order.closedShort ? null : (
            <Button
              size="default"
              tone="brand"
              emphasis="primary"
              action="receipt-save"
              rootAttributes={{ 'data-unsaved-save': '' }}
            >
              Record Receipt
            </Button>
          )}
        </>
      }
    >
      <List gap="m">
        <Text variant="p">
          {order.quantityOrdered === null
            ? `Record what arrived for “${lineDescription}”.`
            : `Record what arrived for “${lineDescription}”. ${String(order.quantityOrdered)} ordered${progress === '' ? '' : `; ${progress.toLowerCase()}`}.`}
        </Text>
        {order.receipts.length > 0 ? (
          <List gap="xs" className="receipt-dialog-history">
            {order.receipts.map((receipt) => (
              <Row vAlign="middle" gap="xs" wrap rootAttributes={{ 'data-key': receipt.id }}>
                <Text variant="span" size="compact">
                  {`${String(receipt.quantityReceived)} received ${orderDay(receipt.receivedAt)}${receipt.note === null ? '' : ` · ${receipt.note}`}`}
                </Text>
                <Button
                  tone="danger"
                  emphasis="plain"
                  size="compact"
                  action="receipt-delete"
                  rootAttributes={{ 'data-receipt-id': receipt.id }}
                >
                  Remove
                </Button>
              </Row>
            ))}
          </List>
        ) : null}
        {order.closedShort ? (
          <Row vAlign="middle" gap="xs" wrap>
            <Text variant="span" size="compact">
              {`The rest was closed short${order.closedShortReason === null ? '.' : `: ${order.closedShortReason}`}`}
            </Text>
            <Button tone="neutral" emphasis="secondary" size="compact" action="receipt-reopen-short">
              Reopen
            </Button>
          </Row>
        ) : (
          <>
            <TextField
              type="number"
              min="0"
              name="receipt-quantity"
              label="Quantity Received"
              value={outstanding === null || outstanding === 0 ? '' : String(outstanding)}
              autofocus
            />
            <TextField type="date" name="receipt-date" label="Received On" value={today} />
            <TextField
              multiline
              name="receipt-note"
              label="Note"
              hint="For example, short or damaged items."
              rows={2}
              maxLength={1000}
            />
            <Checkbox
              name="receipt-close-short"
              hint="Nothing more is expected; the order counts as delivered."
              label="Close the rest short"
            />
            <TextField name="receipt-short-reason" label="Why the Rest Isn't Coming" hint="Optional" maxLength={500} />
          </>
        )}
      </List>
    </DialogSurface>
  );
}
